import React, { useEffect, useRef, useState } from "react";
import { MdSearch } from "react-icons/md";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  fetchWorkEntries,
  fetchWorkEntriesProjectTracking,
} from "../../../../store/slices/workentriesSlice";
import { fetchDescriptionTasksProjectTracking } from "../../../../store/slices/DescriptionTask";
import {
  fetchHardwareDescriptions,
  fetchHardwareProjectTracking,
} from "../../../../store/slices/hardwareDescriptionSlice";
import { fetchDescriptionTasks } from "../../../../store/slices/descriptionTaskSlice";
import { fetchEmployees } from "../../../../store/slices/employeeAdminSlice";
import { fetchAllProjects } from "../../../../store/slices/projectsSlice";
import AdminLoader from "../../../../components/common/loader/AdminLoader";
import OmShowAppprovedTasks from "./OmShowAppprovedTasks";
import FileUploadSales from "../../SalesOperations/SaleOperation/ResponseReceived/FileUploadSales";
import { setProjectReadTrue } from "../../../../store/slices/readSlice";
import DateConversion from "../../../../utils/DateConversion";
import { fetchGroups } from "../../../../store/slices/groupSlice";
import { getMyTrainings } from "../../../../store/slices/MyTraining/myTrainings";

const OmProjectApprovedDash = ({ approvedProjectsManager, handlerefresh, isViewOnly = false }) => {
  const dispatch = useDispatch();
  const { formatDateByCountry } = DateConversion();
  const { employees } = useSelector((state) => state.employees);
  const { projects } = useSelector((state) => state.projects);
  const { group: groups } = useSelector((state) => state.group);
  const { tasks } = useSelector((state) => state.descriptionTasks);
  const { entries } = useSelector((state) => state.workentries);
  const { items } = useSelector((state) => state.hardwareDescription);
  const { mytrainings } = useSelector((state) => state.myTrainings);
  const [currentProject, setCurrentProject] = useState(null);
  const [showAttach, setShowAttach] = useState(false);
  const { hardwareProjectTracking } = useSelector(
    (state) => state.hardwareDescription
  );
  const { descriptionProjectTracking } = useSelector(
    (state) => state.descriptionTasks
  );

  useEffect(() => {
    dispatch(fetchWorkEntriesProjectTracking());
    dispatch(fetchDescriptionTasksProjectTracking());
    dispatch(fetchHardwareProjectTracking());

    dispatch(fetchWorkEntries());
    dispatch(fetchHardwareDescriptions());
    dispatch(fetchDescriptionTasks());
    dispatch(fetchEmployees());
    dispatch(fetchAllProjects());
    dispatch(fetchGroups());
    dispatch(getMyTrainings());
  }, [dispatch]);

  const groupNameById = React.useMemo(() => {
    const map = new Map();
    (groups || []).forEach((g) => {
      const id = g?._id || g?.id;
      const name = g?.newGroupName || g?.groupname || g?.groupName || g?.name || "";
      if (id && name) map.set(String(id), name);
    });
    return map;
  }, [groups]);

  const getDisplayGroupName = React.useCallback(
    (projectRow) => {
      const groupId = projectRow?.groupid?._id || projectRow?.groupid;
      if (groupId && groupNameById.has(String(groupId))) {
        return groupNameById.get(String(groupId));
      }
      return projectRow?.AssignedGroup || "";
    },
    [groupNameById]
  );
  const [isLoading] = useState(false);

  const [sortedProjects, setSortedProjects] = useState([]);

  useEffect(() => {
    setSortedProjects(approvedProjectsManager);
    setCurrentPage(1); // Reset to first page when filtered projects change
  }, [approvedProjectsManager]);

  const itemsPerPage = 10; // or any number of rows you want per page
  const [currentPage, setCurrentPage] = useState(1);

  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setCurrentPage(1);
    filterLeadReports(query)
  };

  const totalItems = sortedProjects.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;
  const paginatedProjects = sortedProjects.slice(startIdx, endIdx);

  const filterLeadReports = (query) => {
    const lowerCaseQuery = query.toLowerCase().trim();

    const filtered = approvedProjectsManager.filter((LeadReport) =>
      getDisplayGroupName(LeadReport).toLowerCase().includes(lowerCaseQuery) ||
      Object.entries(LeadReport).some(([key, value]) => {
        // Skip these keys
        if (
          key === "Assignedperson" ||
          key === "TypeService" ||
          key === "SalesPersonName" ||
          key === "SalesPersonEmail" ||
          key === "ResourceType" ||
          key === "QuotedValue" ||
          key === "ProjectManagerStatus" ||
          key === "ProjectManagerAction" ||
          key === "createdAt" ||
          key === "updatedAt" ||
          key === "PrimaryResources"
        )
          return false;

        if (value === null || value === undefined) return false;
        if (key === "DateRequested" || key === "ProjectDueDate") {
          const formattedDate = formatDateByCountry(value);  // ← change here
          return formattedDate.toLowerCase().includes(lowerCaseQuery);
        }

        // Handle all other fields
        const formattedValue = value.toString().trim().toLowerCase();
        return formattedValue.includes(lowerCaseQuery);
      })
    );

    setSortedProjects(filtered);
  };

  const sortAlphabetically = (targetToSort, order) => {
    const key = `${targetToSort}-${order}`;
    let sorted = [...sortedProjects]; // Always sort from the filtered dataset

    const compareStrings = (a, b) => {
      const valA = (a || "").toString().toLowerCase();
      const valB = (b || "").toString().toLowerCase();
      return valA.localeCompare(valB);
    };
    // Helper function for date comparison
    const compareDates = (a, b) => {
      const dateA = a ? new Date(a) : null;
      const dateB = b ? new Date(b) : null;

      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      return dateA - dateB;
    };

    switch (key) {
      case "AssignedGroup-az":
        sorted.sort((a, b) => compareStrings(getDisplayGroupName(a), getDisplayGroupName(b)));
        break;
      case "AssignedGroup-za":
        sorted.sort((a, b) => compareStrings(getDisplayGroupName(b), getDisplayGroupName(a)));
        break;
      case "ProjectName-az":
        sorted.sort((a, b) => compareStrings(a.ProjectName, b.ProjectName));
        break;
      case "ProjectName-za":
        sorted.sort((a, b) => compareStrings(b.ProjectName, a.ProjectName));
        break;
      case "DateRequested-az":
        sorted.sort((a, b) => compareDates(a.DateRequested, b.DateRequested));
        break;
      case "DateRequested-za":
        sorted.sort((a, b) => compareDates(b.DateRequested, a.DateRequested));
        break;
      case "ProjectDueDate-az":
        sorted.sort((a, b) => compareDates(a.ProjectDueDate, b.ProjectDueDate));
        break;
      case "ProjectDueDate-za":
        sorted.sort((a, b) => compareDates(b.ProjectDueDate, a.ProjectDueDate));
        break;
      default:
        // Default to original filtered order
        sorted = [...sortedProjects];
        break;
    }

    setSortedProjects(sorted);
    setCurrentPage(1); // Reset to first page after sorting
  };

  const handleSalesAction = (row) => {
    if (!row?.Assignedperson?.length) return " ";

    const matched = row.Assignedperson.find((person) =>
      employees.some(
        (emp) =>
          emp.EmpEmail === person.email &&
          emp.customRoles?.includes("project manager")
      )
    );

    if (!matched) return " ";

    // New conditions
    if (matched.manageraction === "cancelled") return "Cancelled";
    if (matched.salespersonaction === "project not-assigned" && matched.salespersonstatus === "project approved") return "Cancelled";
    if (matched.salespersonstatus === "project approved") return "Approved";

    // Existing conditions
    if (matched.salespersonaction === "accept") return "Approved";
    if (matched.salespersonaction === "reject") return "Rejected";

    return " ";
  };


  const [showListState, setShowListState] = useState("tasktable")
  const [rowID, setRowID] = useState(null)
  const [clickedProject, setClickedProject] = useState(null)

  const ProjectApprovedRef = useRef(null);
  useEffect(() => {
    if ((rowID)) {
      // Scroll to the right end of the container
      const scrollToPosition = ProjectApprovedRef.current.scrollWidth;
      ProjectApprovedRef.current.scrollTo({
        left: scrollToPosition,
        behavior: 'smooth'
      });
    }
  }, [rowID]);

  const handleleadread = async (row) => {
    if (isViewOnly) return;
    if (row.isRead === false) {
      const projectId = row._id
      try {
        await dispatch(setProjectReadTrue(projectId));
      } catch (error) {
        console.error("Error updating Notes:", error);
      } finally {
        handlerefresh()
      }
    }
  };

  return (
    <div>
      <>
        {isLoading ? <AdminLoader isLoading={isLoading} /> : <> </>}
        <div className="">
          <div className="flex justify-between items-center p-2 bg-white">
            <div className=" font-semibold">
              <h3>Project Approved</h3>
            </div>

            <div className="flex gap-2 p-1 align-middle items-center bg-[#e0e7ff] rounded-full">
              <MdSearch className=" w-5 h-5 text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchInputChange}
                placeholder="Search"
                className="bg-[#e0e7ff] outline-none w-[80%]"
              />
            </div>
          </div>
          <div
            ref={ProjectApprovedRef}
            className="w-[100%]  bg-white  h-full  min-h-[20vh] max-h-[90vh] overflow-auto pb-5">
            <div className={`${rowID ? "min-w-[1350px]" : "w-auto"}`}>
              <div className="flex w-[100%] bg-[#E8E8E8] text-black text-xs  text-center">
                <div className={`font-semibold ${rowID ? "flex w-[50%]" : "flex w-[84%]"}`}>
                  <div className="h-12 w-[6%] border flex justify-start align-middle items-center pl-2">Sr. No</div>
                  <div className="h-12 w-[16.6%] border p-2 break-words text-wrap flex justify-start items-center align-middle">
                    <p className=" w-[75%] break-words whitespace-normal text-start pl-2">Group</p>
                    <div className="w-[25%] flex flex-col">
                      <div
                        className="cursor-pointer "
                        onClick={() =>
                          sortAlphabetically("AssignedGroup", "az")
                        }
                      >
                        <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                      </div>

                      <div
                        className="cursor-pointer -mt-2"
                        onClick={() =>
                          sortAlphabetically("AssignedGroup", "za")
                        }
                      >
                        <ArrowDropDown
                          color="white"
                          sx={{ fontSize: "22px" }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="h-12 w-[16.6%] border p-2 break-words text-wrap flex justify-start items-center align-middle">
                    <p className="w-[75%] break-words whitespace-normal text-start">Project</p>
                    <div className="w-[25%] flex flex-col">
                      <div
                        className="cursor-pointer "
                        onClick={() => sortAlphabetically("ProjectName", "az")}
                      >
                        <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                      </div>

                      <div
                        className="cursor-pointer -mt-2"
                        onClick={() => sortAlphabetically("ProjectName", "za")}
                      >
                        <ArrowDropDown
                          color="white"
                          sx={{ fontSize: "22px" }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="h-12 w-[18.6%] border p-2 break-words text-wrap flex justify-start items-center align-middle">
                    <p className="w-[75%] break-words whitespace-normal text-start">Date Requested</p>
                    <div className="w-[25%] flex flex-col items-end">
                      <div
                        className="cursor-pointer "
                        onClick={() =>
                          sortAlphabetically("DateRequested", "az")
                        }
                      >
                        <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                      </div>

                      <div
                        className="cursor-pointer -mt-2"
                        onClick={() =>
                          sortAlphabetically("DateRequested", "za")
                        }
                      >
                        <ArrowDropDown
                          color="white"
                          sx={{ fontSize: "22px" }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="h-12 w-[18.6%] relative border p-2 break-words text-wrap flex justify-center items-center align-middle">
                    <p className=" break-words whitespace-normal text-start ">Due Date</p>
                    <div className="absolute right-0 flex flex-col">
                      <div
                        className="cursor-pointer "
                        onClick={() =>
                          sortAlphabetically("ProjectDueDate", "az")
                        }
                      >
                        <ArrowDropUp color="white" sx={{ fontSize: "22px" }} />
                      </div>

                      <div
                        className="cursor-pointer -mt-2"
                        onClick={() =>
                          sortAlphabetically("ProjectDueDate", "za")
                        }
                      >
                        <ArrowDropDown
                          color="white"
                          sx={{ fontSize: "22px" }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="h-12 w-[14.6%] border p-2 break-words text-wrap flex justify-center items-center align-middle">
                    Attachment
                  </div>
                  <div className="h-12 w-[14.6%] border p-2 break-words text-wrap flex justify-center items-center">
                    Action
                  </div>
                </div>
                {rowID ? (
                  <div className="flex w-[50%] font-semibold">
                    <div className="w-[23%] bg-[#E8E8E8] text-black border p-2 break-words text-wrap flex justify-center items-center">
                      Manager Details
                    </div>
                    <div className="w-[77%] bg-[#E8E8E8] text-black break-words text-wrap">
                      <div className="w-full h-[50%] p-2 break-words text-wrap flex justify-center items-center" >
                        Task Details
                      </div>
                      <div className="w-full h-[50%] break-words text-wrap flex ">
                        <div
                          onClick={() => setShowListState("tasktable")}
                          className={`w-[50%] ${showListState === "tasktable" ? "bg-blue-500 text-white" : "text-black"} p-2 cursor-pointer flex justify-center items-center`}
                        >
                          {clickedProject?.TypeService?.toLowerCase().includes("hardware") ? "Part List" : "Effort Expenses"}
                        </div>
                        <div
                          onClick={() => setShowListState("otherexpenses")}
                          className={` w-[50%] ${showListState === "otherexpenses" ? "bg-blue-500 text-white" : "text-black"} p-2 cursor-pointer flex justify-center items-center`}>
                          Other Expenses
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="w-[16%] h-12 font-semibold bg-[#E8E8E8] text-black p-2 border flex justify-center items-center">
                    Task Details
                  </div>

                )}
              </div>
              <div className="w-[100%]">
                {paginatedProjects?.map((row, index) => {
                  return (
                    <div
                      className={`flex ${row.isRead === false ? "font-bold" : " "} text-xs w-full ${isViewOnly ? "cursor-default" : "cursor-pointer hover:bg-gray-100"} `}
                      key={index}
                    >
                      <div className={`${rowID ? "flex w-[50%]" : "flex w-[84%]"} `}>
                        <div onClick={() => {
                          if (isViewOnly) return;
                          const isOpening = rowID !== row?._id;
                          setRowID(rowID === row._id ? null : row._id)
                          setClickedProject(clickedProject === row ? null : row)
                          if (isOpening) {
                            handleleadread(row);
                          }
                        }}
                          className="w-[6%] border-b flex justify-center align-middle items-center pr-1">
                          {startIdx + index + 1}{" "}
                        </div>
                        <div onClick={() => {
                          if (isViewOnly) return;
                          const isOpening = rowID !== row?._id;
                          setRowID(rowID === row._id ? null : row._id)
                          setClickedProject(clickedProject === row ? null : row)
                          if (isOpening) {
                            handleleadread(row);
                          }
                        }}
                          className="w-[16.6%] border-b flex justify-start align-middle items-center p-2 break-words text-wrap">
                         
                          <span className="pl-2"> {getDisplayGroupName(row)}</span>
                        </div>
                        <div onClick={() => {
                          if (isViewOnly) return;
                          const isOpening = rowID !== row?._id;
                          setRowID(rowID === row._id ? null : row._id)
                          setClickedProject(clickedProject === row ? null : row)
                          if (isOpening) {
                            handleleadread(row);
                          }
                        }}
                          className="w-[16.6%] border-b flex justify-start align-middle items-center text-left p-2 break-words whitespace-normal overflow-hidden text-ellipsis line-clamp-2 leading-snug text-xs">
                          {row.ProjectName}
                        </div>
                        <div onClick={() => {
                          if (isViewOnly) return;
                          const isOpening = rowID !== row?._id;
                          setRowID(rowID === row._id ? null : row._id)
                          setClickedProject(clickedProject === row ? null : row)
                          if (isOpening) {
                            handleleadread(row);
                          }
                        }}
                          className="w-[18.6%] border-b flex justify-center align-middle items-center pr-2 break-words text-wrap">
                          {row.DateRequested &&
                            !isNaN(new Date(row.DateRequested))
                            ? formatDateByCountry(row.DateRequested)
                            : " "}
                        </div>
                        <div onClick={() => {
                          if (isViewOnly) return;
                          const isOpening = rowID !== row?._id;
                          setRowID(rowID === row._id ? null : row._id)
                          setClickedProject(clickedProject === row ? null : row)
                          if (isOpening) {
                            handleleadread(row);
                          }
                        }}
                          className="w-[18.6%] border-b flex justify-center align-middle items-center pr-2 break-words text-wrap">
                          {row.ProjectDueDate &&
                            !isNaN(new Date(row.ProjectDueDate))
                            ? formatDateByCountry(row.ProjectDueDate)
                            : " "}
                        </div>
                        <div className="w-[14.6%] border-b flex justify-center align-middle items-center pr-2 text-center break-words text-wrap">
                          <AttachFileIcon className="rotate-12 cursor-pointer"
                            onClick={() => {
                              setCurrentProject(row);
                              setShowAttach(true);
                            }}
                          />

                        </div>
                        <div onClick={() => {
                          if (isViewOnly) return;
                          const isOpening = rowID !== row?._id;
                          setRowID(rowID === row._id ? null : row._id)
                          setClickedProject(clickedProject === row ? null : row)
                          if (isOpening) {
                            handleleadread(row);
                          }
                        }}
                          className="w-[14.6%]  p-2 flex border-b justify-center align-middle items-center text-center break-words text-wrap">
                          {handleSalesAction(row)}
                        </div>
                      </div>

                      <div className={`${rowID ? "flex w-[50%]" : "flex w-[16%]"} border-b `}>
                        {rowID === row._id ? (
                          <OmShowAppprovedTasks
                            row={row}
                            employees={employees}
                            showListState={showListState}
                            projects={projects}
                            entries={
                              entries
                            }
                            descriptionProjectTracking={
                              descriptionProjectTracking
                            }
                            hardwareProjectTracking={hardwareProjectTracking}
                            tasks={tasks}
                            items={items}
                            mytrainings={mytrainings}
                            isViewOnly={isViewOnly}
                          />
                        ) : (
                          <div onClick={() => {
                            if (isViewOnly) return;
                            const isOpening = rowID !== row?._id;
                            setRowID(rowID === row._id ? null : row._id)
                            setClickedProject(clickedProject === row ? null : row)
                            if (isOpening) {
                              handleleadread(row);
                            }
                          }}
                            className={`w-full border-b h-full flex justify-center items-center ${isViewOnly ? "text-[#9ca3af] cursor-not-allowed pointer-events-none" : ""}`}>
                            Multiple
                          </div>
                        )}

                      </div>
                    </div>
                  );
                })}
                {paginatedProjects.length === 0 && (
                  <div className="noDataAvailable p-2">No Result Found</div>
                )}
              </div>
            </div>
          </div>
          <div
            className={`pagination flex justify-between  text-xs items-center bg-white pb-2 pl-2 rounded-md ${paginatedProjects.length > 0 ? "p-1" : ""
              } `}
          >
            <div>
              {totalItems > 0 ? (
                <>
                  Showing {startIdx + 1} to {Math.min(endIdx, totalItems)} of{" "}
                  {totalItems} Entries
                </>
              ) : (
                <>
                  {paginatedProjects.length > 0 && (
                    <>Showing {paginatedProjects.length} Entries</>
                  )}
                </>
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  className="px-4 py-2 mx-2 border rounded"
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                {Array.from({ length: 3 }, (_, index) => {
                  const pageNumber = currentPage - 1 + index;
                  if (pageNumber >= 1 && pageNumber <= totalPages) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`px-4 py-2 mx-2 border rounded ${currentPage === pageNumber ? "bg-gray-300" : ""
                          }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  }
                  return null;
                })}

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  className="px-4 py-2 mx-2 border rounded"
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {showAttach && (
            <FileUploadSales
              currentProject={currentProject}
              setShowAttach={setShowAttach}
              employees={employees}
              isViewOnly={isViewOnly}
            />
          )
          }
        </div>
      </>
    </div>
  );
};

export default OmProjectApprovedDash;
